import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CheckboxField,
  Field,
  PageHeader,
  Skeleton,
  TextField,
  toast,
} from '@eon/ui';
import { resolveApiError, useTranslation } from '@eon/i18n';
import { hooks } from '@eon/api-client';
import type { Station } from '@eon/types';
import { ArrowLeft } from 'lucide-react';
import { MapPicker } from '@/components/portal/MapPicker';

function stationToForm(station: Station) {
  return {
    name: station.name ?? '',
    description: station.description ?? '',
    address: station.address ?? '',
    latitude: station.latitude != null ? String(station.latitude) : '39.92',
    longitude: station.longitude != null ? String(station.longitude) : '32.85',
    city: station.city ?? '',
    country: station.country ?? '',
    isPublic: station.isPublic ?? false,
    isActive: station.isActive ?? station.active ?? true,
  };
}

const defaultForm = () => ({
  name: '',
  description: '',
  address: '',
  latitude: '39.92',
  longitude: '32.85',
  city: '',
  country: '',
  isPublic: false,
  isActive: true,
});

export function StationNewPage() {
  return <PortalStationEditForm />;
}

export function StationEditPage() {
  const { stationId } = useParams({ from: '/_app/stations/$stationId/edit' });
  return <PortalStationEditForm stationId={stationId} />;
}

function PortalStationEditForm({ stationId }: { stationId?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isNew = !stationId;

  const { data: station, isLoading } = hooks.useOmStation(stationId ?? '');
  const create = hooks.useOmCreateStation();
  const update = hooks.useOmUpdateStation();
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (station) setForm(stationToForm(station));
  }, [station]);

  const set = <K extends keyof ReturnType<typeof defaultForm>>(
    k: K,
    v: ReturnType<typeof defaultForm>[K],
  ) => setForm((s) => ({ ...s, [k]: v }));

  const buildPayload = () => {
    const latitude = Number.parseFloat(form.latitude);
    const longitude = Number.parseFloat(form.longitude);
    return {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      address: form.address.trim(),
      city: form.city.trim() || undefined,
      country: form.country.trim() || undefined,
      latitude,
      longitude,
      isPublic: form.isPublic,
      isActive: form.isActive,
    };
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(t('station.create.nameRequired'));
      return;
    }
    const latitude = Number.parseFloat(form.latitude);
    const longitude = Number.parseFloat(form.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      toast.error(t('station.create.coordsRequired'));
      return;
    }
    try {
      const payload = buildPayload();
      if (isNew) {
        const created = await create.mutateAsync(payload);
        const createdId =
          created && typeof created === 'object' && 'id' in created
            ? String((created as { id: unknown }).id)
            : undefined;
        toast.success(t('station.create.success'));
        if (createdId) {
          navigate({ to: '/stations/$stationId', params: { stationId: createdId } });
        } else {
          navigate({ to: '/stations' });
        }
      } else if (stationId) {
        await update.mutateAsync({ stationId, ...payload });
        toast.success(t('station.edit.success'));
        navigate({ to: '/stations/$stationId', params: { stationId } });
      }
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  const busy = create.isPending || update.isPending;

  if (!isNew && isLoading) {
    return (
      <>
        <PageHeader title={t('station.edit.title')} />
        <Skeleton className="h-96 w-full" />
      </>
    );
  }

  const lat = Number.parseFloat(form.latitude);
  const lng = Number.parseFloat(form.longitude);

  return (
    <>
      <PageHeader
        title={isNew ? t('station.create.title') : t('station.edit.title')}
        description={isNew ? t('station.create.description') : station?.name}
        actions={
          <Button variant="outline" asChild>
            <Link to={isNew ? '/stations' : '/stations/$stationId'} params={stationId ? { stationId } : undefined}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('station.create.back')}
            </Link>
          </Button>
        }
      />

      <Card className="max-w-4xl">
        <CardHeader>
          <CardTitle className="text-base">{t('station.create.formTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('station.name')} required>
              <TextField value={form.name} onChange={(e) => set('name', e.target.value)} />
            </Field>
            <div className="sm:col-span-2">
              <Field label={t('station.create.descriptionField')}>
                <TextField value={form.description} onChange={(e) => set('description', e.target.value)} />
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">{t('station.detail.location')}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label={t('station.address')}>
                  <TextField value={form.address} onChange={(e) => set('address', e.target.value)} />
                </Field>
              </div>
              <Field label={t('station.detail.city')}>
                <TextField value={form.city} onChange={(e) => set('city', e.target.value)} />
              </Field>
              <Field label={t('station.detail.country')}>
                <TextField value={form.country} onChange={(e) => set('country', e.target.value)} />
              </Field>
              <Field label={t('station.detail.latitude')} required>
                <TextField
                  value={form.latitude}
                  onChange={(e) => set('latitude', e.target.value)}
                  className="font-mono"
                />
              </Field>
              <Field label={t('station.detail.longitude')} required>
                <TextField
                  value={form.longitude}
                  onChange={(e) => set('longitude', e.target.value)}
                  className="font-mono"
                />
              </Field>
            </div>
            <MapPicker
              latitude={Number.isFinite(lat) ? lat : 39.92}
              longitude={Number.isFinite(lng) ? lng : 32.85}
              onChange={(newLat, newLng) => {
                set('latitude', String(newLat));
                set('longitude', String(newLng));
              }}
              className="overflow-hidden rounded-md border"
            />
          </section>

          <section className="flex flex-wrap gap-4">
            <CheckboxField
              checked={form.isPublic}
              onCheckedChange={(c) => set('isPublic', c)}
              label={t('station.detail.isPublic')}
            />
            <CheckboxField
              checked={form.isActive}
              onCheckedChange={(c) => set('isActive', c)}
              label={t('station.detail.isActive')}
            />
          </section>
        </CardContent>
        <CardFooter className="justify-end gap-2 border-t pt-4">
          <Button variant="outline" asChild disabled={busy}>
            <Link to="/stations">{t('actions.cancel')}</Link>
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy
              ? t('station.create.saving')
              : isNew
                ? t('station.create.submit')
                : t('station.edit.submit')}
          </Button>
        </CardFooter>
      </Card>
    </>
  );
}
